// Today banner — the greeting, overall mastery, and the daily goal ring.
//
// Replaces GardenHero, which drew a sky-to-grass gradient scene with cartoon
// plants. Two reasons it had to go. It broke the design system's own "flat
// only — no gradients, photos or textures" rule, and it aimed the product at
// the wrong age: the users are 15- and 17-year-olds sitting board exams, and
// the screen is also what a parent sees over their shoulder. The mechanic that
// actually drives behaviour — a daily goal, a ring, a streak — is kept intact;
// only the costume is gone.
import { Ring } from './Ring';

function greetingWord(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export interface TodayHeroProps {
  /** First name (or a safe fallback) to greet the student by. */
  name: string;
  /** 0–100, share of cards mastered. */
  pctMastered: number;
  masteredCount: number;
  totalCount: number;
  doneToday: number;
  goalToday: number;
}

export function TodayHero({
  name,
  pctMastered,
  masteredCount,
  totalCount,
  doneToday,
  goalToday,
}: TodayHeroProps) {
  const donePct = goalToday > 0 ? Math.min(100, Math.round((doneToday / goalToday) * 100)) : 0;
  const toGo = Math.max(0, goalToday - doneToday);

  return (
    <div
      className="flex flex-col gap-5 rounded-card p-5 sm:flex-row sm:items-center sm:justify-between"
      style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
    >
      <div className="min-w-0">
        <div
          className="text-fs-11 font-bold uppercase tracking-wide"
          style={{ color: 'var(--text-hint)' }}
        >
          Today
        </div>
        <h2
          className="mt-1 text-fs-26 font-extrabold leading-tight tracking-tight sm:text-fs-30"
          style={{ color: 'var(--text-accent)' }}
        >
          Good {greetingWord()}, {name}
        </h2>
        <p className="mt-1.5 text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
          <span data-numeric="">{pctMastered}%</span> mastered ·{' '}
          <span data-numeric="">
            {masteredCount} of {totalCount}
          </span>{' '}
          concepts secure
        </p>
      </div>

      <div className="flex flex-none items-center gap-3">
        <Ring pct={donePct} big={String(doneToday)} sm={`of ${goalToday}`} tone="good" size={54} />
        <div>
          <div className="text-fs-15 font-extrabold" style={{ color: 'var(--text-strong)' }}>
            Today&rsquo;s goal
          </div>
          <div className="text-fs-12.5 font-semibold" style={{ color: 'var(--text-muted)' }}>
            {toGo > 0 ? `${toGo} more to finish` : 'Finished for today'}
          </div>
        </div>
      </div>
    </div>
  );
}
